/**
 * Live-agent WebSocket contracts (the `/v1/agent` proxy) + server-side tool I/O.
 *
 * Three hops share these shapes:
 *   browser  ◀──WS──▶  NestJS live proxy  ◀──▶  LiveAgentProvider (fake/openai/gemini)
 *
 * - `ClientToServerMessage` — what the browser sends (audio/text/control).
 * - `ServerToClientMessage` — what the proxy streams back
 *   (transcription/audio/toolCall/turnComplete/ready/error).
 * - Tool I/O — `save_feedback` (implicit filing) and `query_site_knowledge`
 *   (grounding). Tools are declared here and EXECUTED server-side with org
 *   context; the browser never runs them.
 *
 * Audio is base64-encoded PCM/opus in the JSON envelope (the widget keeps the
 * transport simple; binary frames can be added later without changing types).
 */
import type { z } from 'zod';
import { array, boolean, discriminatedUnion, email, enum as zEnum, literal, number, object, record, string, unknown, url } from 'zod';

import { CapturedErrorSchema, ConsoleLogSchema, UserActionSchema } from './capture.js';
import { ChannelSchema, SentimentSchema, TranscriptRoleSchema } from './enums.js';

// ─────────────────────────────────────────────────────────────────────────────
// Client → Server (browser → proxy)
// ─────────────────────────────────────────────────────────────────────────────

/** Start a live session. Sent right after the socket opens. */
export const LiveClientHelloSchema = object({
  type: literal('hello'),
  publicKey: string().min(1),
  /** Resume an existing session (reconnect) — corresponds to `?resume=`. */
  resumeSessionId: string().min(1).optional(),
  /** Page context so an implicitly-filed ticket has metadata. */
  pageUrl: url().optional(),
  pageTitle: string().max(1024).optional(),
  lang: string().max(35).optional(),
  /** Preferred channel for this session. */
  channel: ChannelSchema.default('voice'),
});
export type LiveClientHello = z.infer<typeof LiveClientHelloSchema>;

/** A chunk of captured microphone audio. */
export const LiveClientAudioSchema = object({
  type: literal('audio'),
  /** Base64-encoded audio bytes. */
  data: string().min(1),
  /** PCM sample rate, if applicable. */
  sampleRate: number().int().positive().optional(),
  /** MIME of the chunk, e.g. "audio/pcm;rate=16000" or "audio/webm;codecs=opus". */
  mimeType: string().max(128).optional(),
});
export type LiveClientAudio = z.infer<typeof LiveClientAudioSchema>;

/** A typed text turn (text-agent mode, or to supplement voice). */
export const LiveClientTextSchema = object({
  type: literal('text'),
  text: string().min(1).max(16384),
});
export type LiveClientText = z.infer<typeof LiveClientTextSchema>;

/**
 * Out-of-band diagnostics the browser streams so an implicitly-filed ticket has
 * the same context as a normal submit (gated server-side by capture level).
 */
export const LiveClientContextSchema = object({
  type: literal('context'),
  actions: array(UserActionSchema).max(500).optional(),
  consoleLogs: array(ConsoleLogSchema).max(500).optional(),
  errors: array(CapturedErrorSchema).max(200).optional(),
});
export type LiveClientContext = z.infer<typeof LiveClientContextSchema>;

/** Control frames: end-of-turn signal, barge-in/interrupt, graceful close. */
export const LiveClientControlSchema = object({
  type: literal('control'),
  action: zEnum(['commit_turn', 'interrupt', 'close']),
});
export type LiveClientControl = z.infer<typeof LiveClientControlSchema>;

/** Discriminated union of everything the browser can send. */
export const ClientToServerMessageSchema = discriminatedUnion('type', [
  LiveClientHelloSchema,
  LiveClientAudioSchema,
  LiveClientTextSchema,
  LiveClientContextSchema,
  LiveClientControlSchema,
]);
export type ClientToServerMessage = z.infer<typeof ClientToServerMessageSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Server → Client (proxy → browser)
// ─────────────────────────────────────────────────────────────────────────────

/** Session accepted; carries the server session id (= FeedbackSession id). */
export const LiveServerReadySchema = object({
  type: literal('ready'),
  sessionId: string().min(1),
  /** Whether this was a fresh start or a resumed session. */
  resumed: boolean().default(false),
});
export type LiveServerReady = z.infer<typeof LiveServerReadySchema>;

/** Incremental or final transcription of either side. */
export const LiveServerTranscriptionSchema = object({
  type: literal('transcription'),
  role: TranscriptRoleSchema,
  text: string(),
  /** True for streaming partials; false/absent for the finalized turn. */
  partial: boolean().default(false),
});
export type LiveServerTranscription = z.infer<typeof LiveServerTranscriptionSchema>;

/** A chunk of synthesized agent audio to play back. */
export const LiveServerAudioSchema = object({
  type: literal('audio'),
  /** Base64-encoded audio bytes. */
  data: string().min(1),
  sampleRate: number().int().positive().optional(),
  mimeType: string().max(128).optional(),
});
export type LiveServerAudio = z.infer<typeof LiveServerAudioSchema>;

/**
 * A "thought-stream" notice that the agent invoked a server-side tool. The
 * browser renders this in the thought-stream UI; the actual execution + result
 * stay server-side. `args` is the parsed tool input (already validated).
 */
export const LiveServerToolCallSchema = object({
  type: literal('toolCall'),
  /** Provider tool-call id (for correlating a later result, if surfaced). */
  callId: string().min(1),
  name: string().min(1),
  /** Validated tool arguments (shape depends on `name`). */
  args: record(string(), unknown()),
  /** Lifecycle phase for the thought-stream. */
  status: zEnum(['started', 'succeeded', 'failed']).default('started'),
});
export type LiveServerToolCall = z.infer<typeof LiveServerToolCallSchema>;

/** Marks the end of an agent turn. */
export const LiveServerTurnCompleteSchema = object({
  type: literal('turnComplete'),
});
export type LiveServerTurnComplete = z.infer<typeof LiveServerTurnCompleteSchema>;

/**
 * Provider resumption handle update — the proxy persists this on the LiveSession
 * row so a reconnect can resume. Not all providers emit it.
 */
export const LiveServerResumptionUpdateSchema = object({
  type: literal('resumptionUpdate'),
  handle: string().min(1),
});
export type LiveServerResumptionUpdate = z.infer<typeof LiveServerResumptionUpdateSchema>;

/** Error / termination notice. */
export const LiveServerErrorSchema = object({
  type: literal('error'),
  code: string().min(1),
  message: string(),
  /** If true, the socket will close after this frame. */
  fatal: boolean().default(false),
});
export type LiveServerError = z.infer<typeof LiveServerErrorSchema>;

/** Discriminated union of everything the proxy streams to the browser. */
export const ServerToClientMessageSchema = discriminatedUnion('type', [
  LiveServerReadySchema,
  LiveServerTranscriptionSchema,
  LiveServerAudioSchema,
  LiveServerToolCallSchema,
  LiveServerTurnCompleteSchema,
  LiveServerResumptionUpdateSchema,
  LiveServerErrorSchema,
]);
export type ServerToClientMessage = z.infer<typeof ServerToClientMessageSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Server-side tools (declared here, executed in the proxy with org context)
// ─────────────────────────────────────────────────────────────────────────────

/** Canonical tool names the live agent can call. */
export const LiveToolName = {
  SaveFeedback: 'save_feedback',
  QuerySiteKnowledge: 'query_site_knowledge',
} as const;
export type LiveToolName = (typeof LiveToolName)[keyof typeof LiveToolName];

/**
 * `save_feedback` — the implicit-filing path. Idempotent upsert/finalize of the
 * FeedbackSession bound to this live connection. The agent fills the structured
 * fields; the proxy merges them with the connection's captured context + org id.
 */
export const SaveFeedbackToolInputSchema = object({
  /** Short title for the ticket. */
  title: string().min(1).max(512),
  /** A concise summary of the user's feedback/issue. */
  summary: string().min(1).max(8192),
  /** Coarse category (e.g. "bug", "feature_request", "question"). */
  category: string().max(128).optional(),
  sentiment: SentimentSchema.optional(),
  /** Reporter email if the user provided one in conversation (opt-in implied). */
  endUserEmail: email().max(320).optional(),
  /** Whether to mark the session finalized (end of conversation). */
  finalize: boolean().default(true),
});
export type SaveFeedbackToolInput = z.infer<typeof SaveFeedbackToolInputSchema>;

/** Result returned to the agent after `save_feedback`. */
export const SaveFeedbackToolOutputSchema = object({
  sessionId: string().min(1),
  saved: literal(true),
});
export type SaveFeedbackToolOutput = z.infer<typeof SaveFeedbackToolOutputSchema>;

/**
 * `query_site_knowledge` — embed `query`, run an org-scoped VectorIndex search,
 * return top-k chunks for grounding. Never crosses tenants (org id is injected
 * by the proxy, NOT supplied by the model).
 */
export const QuerySiteKnowledgeToolInputSchema = object({
  query: string().min(1).max(4096),
  /** Number of chunks to return. */
  k: number().int().min(1).max(20).default(5),
});
export type QuerySiteKnowledgeToolInput = z.infer<typeof QuerySiteKnowledgeToolInputSchema>;

/** A single retrieved knowledge chunk. */
export const KnowledgeChunkResultSchema = object({
  id: string().min(1),
  text: string(),
  sourceUrl: string().optional(),
  sourceTitle: string().nullable().optional(),
  /** Cosine similarity score (0..1). */
  score: number(),
});
export type KnowledgeChunkResult = z.infer<typeof KnowledgeChunkResultSchema>;

/** Result of `query_site_knowledge`. */
export const QuerySiteKnowledgeToolOutputSchema = object({
  chunks: array(KnowledgeChunkResultSchema),
});
export type QuerySiteKnowledgeToolOutput = z.infer<typeof QuerySiteKnowledgeToolOutputSchema>;

/**
 * Generic envelope for a tool result the proxy hands back to a provider
 * (`sendToolResponse`). Discriminated by tool `name`.
 */
export const ToolResponseSchema = discriminatedUnion('name', [
  object({
    name: literal(LiveToolName.SaveFeedback),
    callId: string().min(1),
    output: SaveFeedbackToolOutputSchema,
  }),
  object({
    name: literal(LiveToolName.QuerySiteKnowledge),
    callId: string().min(1),
    output: QuerySiteKnowledgeToolOutputSchema,
  }),
]);
export type ToolResponse = z.infer<typeof ToolResponseSchema>;
